/**
 * AWS Service Analysis Service
 *
 * Provides AI-powered analysis capabilities for AWS services using Amazon Bedrock.
 * Supports multiple Bedrock models (Nova, Claude, etc.) for flexible analysis.
 * Simplified synchronous API - no polling, no job tracking.
 */

import axios from 'axios';

/**
 * Configuration for analysis service
 */
const ANALYSIS_CONFIG = {
  // API base URL - can be overridden by environment variable
  apiBaseUrl: process.env.REACT_APP_ANALYSIS_API_BASE_URL || null,

  // Request timeout (analysis may take longer than summarization)
  timeout: 120000, // 120 seconds (2 minutes)

  // Retry configuration for network issues only
  maxRetries: 2,
  retryDelay: 2000, // 2 seconds

  // Request headers
  headers: {
    'Content-Type': 'application/json',
  }
};

/**
 * Service endpoints mapping
 */
const SERVICE_ENDPOINTS = {
  lambda: '/analyze/lambda',
  lexV2: '/analyze/lex-v2',
  connect: '/analyze/connect'
};

/**
 * Sleep utility for retry delays
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Get the analysis API base URL from config or environment
 */
const getAnalysisApiBaseUrl = () => {
  const url = ANALYSIS_CONFIG.apiBaseUrl;

  if (!url) {
    throw new Error(
      'Analysis API URL not configured. ' +
      'Please set REACT_APP_ANALYSIS_API_BASE_URL environment variable.'
    );
  }

  return url;
};

/**
 * Check if analysis is available
 */
const checkAnalysisAvailability = () => {
  try {
    getAnalysisApiBaseUrl();
    return true;
  } catch (error) {
    console.warn('Analysis not available:', error.message);
    return false;
  }
};

/**
 * Extract error message from axios error or generic error
 */
const extractErrorMessage = (error) => {
  if (error.response) {
    // API returned an error response
    const data = error.response.data;
    return data.error || data.details || data.message || 'Unknown API error';
  } else if (error.request) {
    // Request made but no response
    return 'No response from analysis service (network error)';
  } else {
    // Other error
    return error.message || 'Unknown error';
  }
};

/**
 * Analyze a service using Bedrock (synchronous)
 *
 * @param {string} serviceName - Service name (lambda, lexV2, connect)
 * @param {object} serviceData - Service-specific data to analyze
 * @param {string} modelId - Bedrock model ID to use
 * @param {object} options - Optional configuration
 * @param {number} options.maxTokens - Maximum tokens to generate
 * @param {number} options.temperature - Temperature for generation
 * @returns {Promise<object>} Analysis result
 */
const analyzeService = async (serviceName, serviceData, modelId, options = {}) => {
  // Validate inputs
  if (!serviceName || !SERVICE_ENDPOINTS[serviceName]) {
    throw new Error(`Invalid service name: ${serviceName}`);
  }

  if (!serviceData || typeof serviceData !== 'object') {
    throw new Error('Valid service data is required for analysis');
  }

  if (!modelId) {
    throw new Error('Model ID is required for analysis');
  }

  const baseUrl = getAnalysisApiBaseUrl();
  const endpoint = `${baseUrl}${SERVICE_ENDPOINTS[serviceName]}`;
  let lastError = null;

  // Retry logic for network issues
  for (let attempt = 1; attempt <= ANALYSIS_CONFIG.maxRetries; attempt++) {
    try {
      const startTime = Date.now();

      console.log(`[${attempt}/${ANALYSIS_CONFIG.maxRetries}] Requesting analysis for ${serviceName} with model ${modelId}`);

      // Build headers with optional auth token
      const headers = { ...ANALYSIS_CONFIG.headers };
      if (options.accessToken) {
        headers['Authorization'] = `Bearer ${options.accessToken}`;
      }

      // Single synchronous POST request to Bedrock-based API
      const response = await axios.post(
        endpoint,
        {
          serviceData,
          modelId,
          maxTokens: options.maxTokens,
          temperature: options.temperature
        },
        {
          timeout: ANALYSIS_CONFIG.timeout,
          headers
        }
      );

      const duration = Date.now() - startTime;
      const bedrockDuration = response.data.metadata?.durationMs || 'N/A';

      console.log(`✅ Analysis received in ${(duration / 1000).toFixed(2)}s (Bedrock: ${bedrockDuration}ms)`);

      // Return result
      return {
        success: true,
        serviceName: response.data.serviceName,
        analysis: response.data.analysis,
        metadata: {
          ...response.data.metadata,
          totalDuration: duration
        }
      };

    } catch (error) {
      lastError = error;
      const errorMsg = extractErrorMessage(error);

      console.error(`❌ Attempt ${attempt} failed for ${serviceName}:`, errorMsg);

      // Retry on network errors, but not on 4xx client errors
      if (error.response && error.response.status >= 400 && error.response.status < 500) {
        // Client error (4xx) - don't retry
        throw new Error(`Failed to analyze ${serviceName}: ${errorMsg}`);
      }

      // Retry on network errors or 5xx server errors
      if (attempt < ANALYSIS_CONFIG.maxRetries) {
        console.log(`Retrying in ${ANALYSIS_CONFIG.retryDelay}ms...`);
        await sleep(ANALYSIS_CONFIG.retryDelay);
      }
    }
  }

  // All retries failed
  const errorMessage = extractErrorMessage(lastError);
  throw new Error(`Failed to analyze ${serviceName} after ${ANALYSIS_CONFIG.maxRetries} attempts: ${errorMessage}`);
};

/**
 * Analyze multiple services sequentially
 *
 * @param {Array<object>} services - Array of {serviceName, serviceData, modelId} objects
 * @param {function} onProgress - Progress callback (serviceName, current, total)
 * @returns {Promise<object>} Results map
 */
const analyzeMultipleServices = async (services, onProgress = null) => {
  console.log(`Starting batch analysis of ${services.length} services...`);

  const results = {};

  for (let i = 0; i < services.length; i++) {
    const service = services[i];
    const serviceName = service.serviceName;

    try {
      // Call progress callback
      if (onProgress) {
        onProgress(serviceName, i + 1, services.length);
      }

      // Analyze service
      const result = await analyzeService(serviceName, service.serviceData, service.modelId, service.options);
      results[serviceName] = result;

      console.log(`✅ [${i + 1}/${services.length}] Completed: ${serviceName}`);

    } catch (error) {
      console.error(`❌ [${i + 1}/${services.length}] Failed: ${serviceName}`, error.message);

      // Store error result
      results[serviceName] = {
        success: false,
        serviceName,
        error: error.message,
        analysis: null
      };
    }
  }

  console.log(`Batch analysis complete. Success: ${Object.values(results).filter(r => r.success).length}/${services.length}`);

  return results;
};

// Create service object
const analysisService = {
  checkAnalysisAvailability,
  analyzeService,
  analyzeMultipleServices,
  ANALYSIS_CONFIG,
  SERVICE_ENDPOINTS
};

// Export service functions
export default analysisService;

// Named exports for convenience
export {
  checkAnalysisAvailability,
  analyzeService,
  analyzeMultipleServices,
  ANALYSIS_CONFIG,
  SERVICE_ENDPOINTS
};
